import { ArrowLeft, Server } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import type { SshProfile } from '@/OS/types/ssh';
import { SshProfileManager } from './SshProfileManager';

interface SshConfigAppProps {
  profiles: SshProfile[];
  onSaveProfile: (profile: SshProfile) => void;
  onDeleteProfile: (profileId: string) => void;
  onExit: () => void;
  isElectron: boolean;
}

export function SshConfigApp({
  profiles,
  onSaveProfile,
  onDeleteProfile,
  onExit,
  isElectron,
}: SshConfigAppProps) {
  return (
    <div className="flex h-full flex-col text-slate-100">
      <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-md border border-cyan-400/20 bg-cyan-400/10 text-cyan-300">
            <Server className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="font-mono text-sm font-semibold uppercase tracking-[0.2em] text-cyan-300">SSH Config</h2>
              <Badge variant="outline" className="border-white/10 bg-white/5 text-[10px] text-slate-300">
                {profiles.length} profile{profiles.length === 1 ? '' : 's'}
              </Badge>
              <Badge variant="outline" className="border-white/10 bg-white/5 text-[10px] text-slate-300">
                {isElectron ? 'desktop ssh enabled' : 'browser-only editing'}
              </Badge>
            </div>
            <p className="truncate text-xs text-slate-400">
              Save SSH targets for MapleOS Terminal and DeMythos. Connect with <span className="font-mono text-slate-300">ssh profile-name</span> or grant one in DeMythos with <span className="font-mono text-slate-300">/ssh profile-name</span>.
            </p>
          </div>
        </div>

        <Button variant="outline" className="border-white/10 bg-white/5 text-slate-100 hover:bg-white/10" onClick={onExit}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Exit
        </Button>
      </div>

      <div className="min-h-0 flex-1 p-4">
        <Card className="flex h-full min-h-0 flex-col overflow-hidden border-white/10 bg-black/30 p-4 shadow-none">
          <div className="min-h-0 flex-1 overflow-y-auto">
            <SshProfileManager
              profiles={profiles}
              onSaveProfile={onSaveProfile}
              onDeleteProfile={onDeleteProfile}
              isElectron={isElectron}
            />
          </div>
        </Card>
      </div>
    </div>
  );
}
